import React, { useCallback, useMemo, useState } from "react";
import styles from "./CronScheduler.module.scss";
import classnames from "classnames";
import type { CronLocale, CronPeriod, CronSchedulerProps } from "./types";
import { Popover } from "../Popover";

const DEFAULT_LOCALE: Required<CronLocale> = {
  everyText: "every",
  emptyWeekDays: "every day of the week",
  emptyWeekDaysShort: "day of the week",
  emptyHours: "every hour",
  emptyMinutes: "every minute",
  emptyMinutesForHourPeriod: "every",
  weekOption: "week",
  dayOption: "day",
  hourOption: "hour",
  prefixPeriod: "Every",
  prefixWeekDays: "on",
  prefixHours: "at",
  prefixMinutes: ":",
  prefixMinutesForHourPeriod: "at",
  suffixMinutesForHourPeriod: "minute(s)",
  errorInvalidCron: "Invalid cron expression",
  weekDays: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
  altWeekDays: ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"],
};

const DEFAULT_PERIODS: CronPeriod[] = ["week", "day", "hour"];

function parseField(field: string, max: number): number[] {
  if (field === "*") return [];
  const values = new Set<number>();
  for (const part of field.split(",")) {
    if (part.includes("-")) {
      const [start, end] = part.split("-").map(Number);
      for (let i = start; i <= end; i++) values.add(i);
    } else if (part.includes("/")) {
      const [, step] = part.split("/").map(Number);
      for (let i = 0; i <= max; i += step) values.add(i);
    } else {
      values.add(Number(part));
    }
  }
  return Array.from(values).sort((a, b) => a - b);
}

function inferPeriod(value: string, allowedPeriods: CronPeriod[]): CronPeriod {
  const parts = value.trim().split(/\s+/);
  const hourField = parts[1] ?? "*";
  const weekDayField = parts[4] ?? "*";

  if (weekDayField !== "*" && allowedPeriods.includes("week")) return "week";
  if (hourField !== "*" && allowedPeriods.includes("day")) return "day";
  return allowedPeriods.includes("hour") ? "hour" : allowedPeriods[0];
}

function parseCronFields(value: string): { minutes: number[]; hours: number[]; weekDays: number[] } {
  const parts = value.trim().split(/\s+/);
  return {
    minutes: parseField(parts[0] ?? "*", 59),
    hours: parseField(parts[1] ?? "*", 23),
    weekDays: parseField(parts[4] ?? "*", 6),
  };
}

function buildCron(period: CronPeriod, minutes: number[], hours: number[], weekDays: number[]): string {
  const minuteField = minutes.length > 0 ? minutes.join(",") : "0";
  const hourField = period === "hour" ? "*" : hours.length > 0 ? hours.join(",") : "0";
  const weekDayField = period === "week" && weekDays.length > 0 ? weekDays.join(",") : "*";
  return `${minuteField} ${hourField} * * ${weekDayField}`;
}

type MultiSelectProps = {
  values: number[];
  options: { value: number; label: string }[];
  onChange: (values: number[]) => void;
  placeholder: string;
};

const MultiSelect: React.FC<MultiSelectProps> = ({ values, options, onChange, placeholder }) => {
  const toggle = (val: number) => {
    const next = values.includes(val) ? values.filter((v) => v !== val) : [...values, val].sort((a, b) => a - b);
    onChange(next);
  };

  const displayText = values.length > 0 ? values.map((v) => options.find((o) => o.value === v)?.label ?? v).join(", ") : placeholder;

  return (
    <Popover
      placement="bottom-right"
      offset={4}
      content={({ triggerRef }) => (
        <div className={styles.multiSelectDropdown} style={{ minWidth: triggerRef.current?.getBoundingClientRect().width }}>
          {options.map((opt) => (
            <label key={opt.value} className={styles.multiSelectOption}>
              <input type="checkbox" checked={values.includes(opt.value)} onChange={() => toggle(opt.value)} />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      )}
    >
      {({ isOpen, triggerRef, togglePopover }) => (
        <div className={styles.multiSelect}>
          <div
            ref={(element) => {
              triggerRef.current = element;
            }}
            className={classnames(styles.multiSelectTrigger, { [styles.open]: isOpen })}
            onClick={togglePopover}
          >
            <span className={classnames({ [styles.placeholder]: values.length === 0 })}>{displayText}</span>
            <span className={styles.arrow}>&#9662;</span>
          </div>
        </div>
      )}
    </Popover>
  );
};

type SingleSelectProps = {
  value: number | string;
  options: { value: number | string; label: string }[];
  onChange: (value: string) => void;
};

const SingleSelect: React.FC<SingleSelectProps> = ({ value, options, onChange }) => {
  return (
    <select className={styles.select} value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
};

export const CronScheduler: React.FC<CronSchedulerProps> = ({ value, setValue, locale: localeProp, allowedPeriods = DEFAULT_PERIODS, className }) => {
  const locale = useMemo(() => ({ ...DEFAULT_LOCALE, ...localeProp }), [localeProp]);

  const [period, setPeriod] = useState<CronPeriod>(() => inferPeriod(value, allowedPeriods));
  const [minutes, setMinutes] = useState<number[]>(() => parseCronFields(value).minutes);
  const [hours, setHours] = useState<number[]>(() => parseCronFields(value).hours);
  const [weekDays, setWeekDays] = useState<number[]>(() => parseCronFields(value).weekDays);

  const periodOptions = useMemo(
    () =>
      allowedPeriods.map((p) => ({
        value: p,
        label: locale[`${p}Option` as keyof CronLocale] as string,
      })),
    [allowedPeriods, locale]
  );

  const hourOptions = useMemo(
    () =>
      Array.from({ length: 24 }, (_, i) => ({
        value: i,
        label: String(i).padStart(2, "0"),
      })),
    []
  );

  const minuteOptions = useMemo(
    () =>
      Array.from({ length: 60 }, (_, i) => ({
        value: i,
        label: String(i).padStart(2, "0"),
      })),
    []
  );

  const weekDayOptions = useMemo(
    () =>
      locale.weekDays.map((name, i) => ({
        value: i,
        label: name,
      })),
    [locale.weekDays]
  );

  const handlePeriodChange = useCallback(
    (newPeriod: string) => {
      const p = newPeriod as CronPeriod;
      setPeriod(p);
      const mins = minutes.length > 0 ? minutes : [0];
      const hrs = hours.length > 0 ? hours : [0];
      setValue(buildCron(p, mins, hrs, weekDays));
    },
    [minutes, hours, weekDays, setValue]
  );

  const handleMinutesChange = useCallback(
    (vals: number[]) => {
      setMinutes(vals);
      setValue(buildCron(period, vals, hours, weekDays));
    },
    [period, hours, weekDays, setValue]
  );

  const handleHoursChange = useCallback(
    (vals: number[]) => {
      setHours(vals);
      setValue(buildCron(period, minutes, vals, weekDays));
    },
    [period, minutes, weekDays, setValue]
  );

  const handleWeekDaysChange = useCallback(
    (vals: number[]) => {
      setWeekDays(vals);
      setValue(buildCron(period, minutes, hours, vals));
    },
    [period, minutes, hours, setValue]
  );

  return (
    <div className={classnames(styles.cronScheduler, className)}>
      <span className={styles.label}>{locale.prefixPeriod}</span>
      <SingleSelect value={period} options={periodOptions} onChange={handlePeriodChange} />

      {period === "week" && (
        <>
          <span className={styles.label}>{locale.prefixWeekDays}</span>
          <MultiSelect values={weekDays} options={weekDayOptions} onChange={handleWeekDaysChange} placeholder={locale.emptyWeekDays} />
        </>
      )}

      {period !== "hour" && (
        <>
          <span className={styles.label}>{locale.prefixHours}</span>
          <MultiSelect values={hours} options={hourOptions} onChange={handleHoursChange} placeholder={locale.emptyHours} />
        </>
      )}

      {period !== "hour" ? (
        <>
          <span className={styles.label}>{locale.prefixMinutes}</span>
          <MultiSelect values={minutes} options={minuteOptions} onChange={handleMinutesChange} placeholder={locale.emptyMinutes} />
        </>
      ) : (
        <>
          <span className={styles.label}>{locale.prefixMinutesForHourPeriod}</span>
          <MultiSelect values={minutes} options={minuteOptions} onChange={handleMinutesChange} placeholder={locale.emptyMinutesForHourPeriod} />
          <span className={styles.label}>{locale.suffixMinutesForHourPeriod}</span>
        </>
      )}
    </div>
  );
};
